import { useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useServerFn } from '@tanstack/react-start'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listLeads,
  decideLead,
  getMyRole,
  listAuthUsers,
  adminDeleteUser,
  deleteLead,
} from '@/lib/admin.functions'

import { supabase } from '@/integrations/supabase/client'
import { LogoMark } from '@/components/skl/Logo'
import { ArrowLeft, LogOut, Mail, Phone, Check, X, Loader2, Trash2, Users, Inbox } from 'lucide-react'


export const Route = createFileRoute('/_authenticated/admin')({
  component: AdminPage,
  head: () => ({
    meta: [
      { title: 'Admin — SKL-Flow' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
})

type Lead = {
  id: string
  navn: string
  virksomhed: string
  branche: string
  kontakt: string
  kunde_email: string | null
  besked: string | null
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  decided_at: string | null
}

function AdminPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const roleFn = useServerFn(getMyRole)
  const leadsFn = useServerFn(listLeads)
  const decideFn = useServerFn(decideLead)

  const [selected, setSelected] = useState<string | null>(null)
  const [tab, setTab] = useState<'leads' | 'users'>('leads')

  const roleQ = useQuery({ queryKey: ['my-role'], queryFn: () => roleFn() })
  const leadsQ = useQuery({
    queryKey: ['admin-leads'],
    queryFn: () => leadsFn(),
    enabled: roleQ.data?.isAdmin === true,
  })

  const usersFn = useServerFn(listAuthUsers)
  const deleteUserFn = useServerFn(adminDeleteUser)
  const usersQ = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => usersFn(),
    enabled: roleQ.data?.isAdmin === true && tab === 'users',
  })
  const deleteUserM = useMutation({
    mutationFn: (userId: string) => deleteUserFn({ data: { userId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const decideM = useMutation({
    mutationFn: (vars: { leadId: string; status: 'approved' | 'rejected' }) =>
      decideFn({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-leads'] })
    },
  })

  const deleteLeadFn = useServerFn(deleteLead)
  const deleteLeadM = useMutation({
    mutationFn: (leadId: string) => deleteLeadFn({ data: { leadId } }),
    onSuccess: () => {
      setSelected(null)
      qc.invalidateQueries({ queryKey: ['admin-leads'] })
    },
  })



  async function handleLogout() {
    await qc.cancelQueries()
    qc.clear()
    await supabase.auth.signOut()
    navigate({ to: '/auth', replace: true })
  }

  if (roleQ.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!roleQ.data?.isAdmin) {
    navigate({ to: '/', replace: true })
    return null
  }


  const leads = (leadsQ.data?.leads ?? []) as Lead[]
  const current = leads.find((l) => l.id === selected) ?? null

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-card/80 border-b border-foreground/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark className="h-7 w-7" />
            <span className="font-display font-bold tracking-tight">SKL-Flow · Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Forside
            </Link>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 px-3 py-1.5 text-sm hover:bg-card/60"
            >
              <LogOut className="h-4 w-4" /> Log ud
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-8 py-8">
        <div className="mb-6 inline-flex rounded-full border border-foreground/10 bg-card/40 p-1">
          <button
            onClick={() => setTab('leads')}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
              tab === 'leads' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Inbox className="h-4 w-4" /> Forespørgsler
          </button>
          <button
            onClick={() => setTab('users')}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition ${
              tab === 'users' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="h-4 w-4" /> Brugere
          </button>
        </div>

        {tab === 'users' ? (
          <UsersPanel
            users={usersQ.data?.users ?? []}
            loading={usersQ.isLoading}
            error={usersQ.error as Error | null}
            deleting={deleteUserM.isPending ? (deleteUserM.variables as string | undefined) ?? null : null}
            onDelete={(id) => deleteUserM.mutate(id)}
            deleteError={deleteUserM.error as Error | null}
          />
        ) : (
          <>
        <h1 className="font-display text-3xl font-bold mb-1">Forespørgsler</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Alle indkomne kontaktforespørgsler. Klik på en sag for detaljer.
        </p>


        {leadsQ.isLoading && (
          <div className="text-muted-foreground text-sm">Indlæser…</div>
        )}

        {leadsQ.data && leads.length === 0 && (
          <div className="rounded-2xl border border-foreground/10 bg-card/40 p-10 text-center text-muted-foreground">
            Ingen forespørgsler endnu.
          </div>
        )}

        {leads.length > 0 && (
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
            <div className="rounded-2xl border border-foreground/10 bg-card/40 divide-y divide-foreground/10 overflow-hidden">
              {leads.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setSelected(l.id)}
                  className={`w-full text-left px-5 py-4 hover:bg-card/60 transition ${
                    selected === l.id ? 'bg-card/70' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{l.virksomhed}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {l.navn} · {l.branche}
                      </div>
                    </div>
                    <StatusBadge status={l.status} />
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {new Date(l.created_at).toLocaleString('da-DK', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </div>
                </button>
              ))}
            </div>

            <div className="rounded-2xl border border-foreground/10 bg-card/40 p-6 lg:sticky lg:top-24 h-fit">
              {!current ? (
                <p className="text-sm text-muted-foreground">
                  Vælg en forespørgsel til venstre.
                </p>
              ) : (
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display text-2xl font-bold">
                        {current.virksomhed}
                      </h2>
                      <p className="text-sm text-muted-foreground">{current.branche}</p>
                    </div>
                    <StatusBadge status={current.status} />
                  </div>

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <Field label="Kontaktperson">{current.navn}</Field>
                    <Field label="Modtaget">
                      {new Date(current.created_at).toLocaleString('da-DK', {
                        dateStyle: 'long',
                        timeStyle: 'short',
                      })}
                    </Field>
                    <Field label="Kontaktoplysning">
                      <span className="inline-flex items-center gap-1.5">
                        {current.kontakt.includes('@') ? (
                          <Mail className="h-4 w-4 text-accent" />
                        ) : (
                          <Phone className="h-4 w-4 text-accent" />
                        )}
                        {current.kontakt}
                      </span>
                    </Field>
                    <Field label="Kunde-email">
                      {current.kunde_email ?? (
                        <span className="text-muted-foreground italic">
                          Ikke tilgængelig
                        </span>
                      )}
                    </Field>
                  </dl>

                  {current.besked && (
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                        Besked
                      </div>
                      <div className="rounded-xl border border-foreground/10 bg-background/40 p-4 whitespace-pre-wrap text-sm">
                        {current.besked}
                      </div>
                    </div>
                  )}

                  {current.status === 'pending' ? (
                    <div className="pt-2">
                      {!current.kunde_email && (
                        <p className="text-xs text-muted-foreground mb-3">
                          Bemærk: kundens e-mail kunne ikke aflæses fra
                          kontaktoplysningerne. Beslutningen registreres, men der bliver
                          ikke sendt en mail.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-3">
                        <button
                          disabled={decideM.isPending}
                          onClick={() =>
                            decideM.mutate({
                              leadId: current.id,
                              status: 'approved',
                            })
                          }
                          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[0_0_24px_-4px_color-mix(in_oklab,var(--primary)_60%,transparent)] hover:bg-primary/90 disabled:opacity-60"
                        >
                          <Check className="h-4 w-4" /> Godkend
                        </button>
                        <button
                          disabled={decideM.isPending}
                          onClick={() =>
                            decideM.mutate({
                              leadId: current.id,
                              status: 'rejected',
                            })
                          }
                          className="inline-flex items-center gap-2 rounded-full border border-destructive/40 text-destructive px-5 py-2.5 text-sm font-semibold hover:bg-destructive/10 disabled:opacity-60"
                        >
                          <X className="h-4 w-4" /> Afslå
                        </button>
                      </div>
                      {decideM.isError && (
                        <p className="mt-3 text-sm text-destructive">
                          {(decideM.error as Error)?.message ?? 'Kunne ikke opdatere'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="pt-2 text-sm text-muted-foreground">
                      Beslutning truffet{' '}
                      {current.decided_at
                        ? new Date(current.decided_at).toLocaleString('da-DK', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })
                        : ''}
                      .
                    </div>
                  )}

                  <div className="pt-4 border-t border-foreground/10">
                    <button
                      disabled={deleteLeadM.isPending}
                      onClick={() => {
                        if (confirm('Slet denne forespørgsel permanent?')) {
                          deleteLeadM.mutate(current.id)
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-full border border-destructive/40 text-destructive px-4 py-2 text-xs font-semibold hover:bg-destructive/10 disabled:opacity-60"
                    >
                      {deleteLeadM.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                      Slet forespørgsel
                    </button>
                    {deleteLeadM.isError && (
                      <p className="mt-2 text-sm text-destructive">
                        {(deleteLeadM.error as Error)?.message ?? 'Kunne ikke slette'}
                      </p>
                    )}
                  </div>
                </div>

              )}
            </div>
          </div>
        )}
          </>
        )}
      </main>

    </div>
  )
}

function StatusBadge({ status }: { status: Lead['status'] }) {
  const map = {
    pending: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
    approved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
  }
  const label = {
    pending: 'Afventer',
    approved: 'Godkendt',
    rejected: 'Afslået',
  }
  return (
    <span
      className={`shrink-0 inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${map[status]}`}
    >
      {label[status]}
    </span>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  )
}

type AuthUser = {
  id: string
  email: string | null
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  full_name: string | null
  virksomhed: string | null
}

function UsersPanel({
  users,
  loading,
  error,
  deleting,
  onDelete,
  deleteError,
}: {
  users: AuthUser[]
  loading: boolean
  error: Error | null
  deleting: string | null
  onDelete: (id: string) => void
  deleteError: Error | null
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null)

  return (
    <div>
      <h1 className="font-display text-3xl font-bold mb-1">Brugere</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Administrer registrerede brugerkonti. Sletning kan ikke fortrydes.
      </p>

      {loading && <div className="text-muted-foreground text-sm">Indlæser…</div>}
      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error.message}
        </div>
      )}
      {deleteError && (
        <div className="mb-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {deleteError.message}
        </div>
      )}

      {!loading && users.length === 0 && !error && (
        <div className="rounded-2xl border border-foreground/10 bg-card/40 p-10 text-center text-muted-foreground">
          Ingen brugere endnu.
        </div>
      )}

      {users.length > 0 && (
        <div className="rounded-2xl border border-foreground/10 bg-card/40 divide-y divide-foreground/10 overflow-hidden">
          {users.map((u) => (
            <div key={u.id} className="px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="font-semibold truncate">
                  {u.full_name || u.email || u.id}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {u.email}
                  {u.virksomhed ? ` · ${u.virksomhed}` : ''}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Oprettet{' '}
                  {new Date(u.created_at).toLocaleString('da-DK', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                  {u.email_confirmed_at ? '' : ' · Ikke bekræftet'}
                </div>
              </div>
              {confirmId === u.id ? (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    disabled={deleting === u.id}
                    onClick={() => {
                      onDelete(u.id)
                      setConfirmId(null)
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-destructive text-destructive-foreground px-3 py-1.5 text-xs font-semibold hover:bg-destructive/90 disabled:opacity-60"
                  >
                    {deleting === u.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Bekræft
                  </button>
                  <button
                    onClick={() => setConfirmId(null)}
                    className="rounded-full border border-foreground/15 px-3 py-1.5 text-xs hover:bg-card/60"
                  >
                    Annuller
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmId(u.id)}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-destructive/40 text-destructive px-3 py-1.5 text-xs font-semibold hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Slet
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

